import React, { FC, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import { Formik, getIn } from 'formik';
import * as Yup from 'yup';
import { Alert, Button, Modal } from '@clearcover/atlas';
import { useAuth0 } from 'Auth';
import { useLazyBaseApi } from 'Shared/services/Api';
import { updatePartnerAncestryPolicy } from 'Admin/authPolicies';
import { PartnerType, Typeahead, TypeaheadResult } from 'Admin/Partners';
import {
  PartnerTypeaheadResponse,
  getFetchPartnersConfig,
} from 'Admin/services/endpoints/PostAttributionTypeahead';
import styles from './styles.module.css';
import { HRule, useToast } from 'Shared/components';

type MovePartnerFormType = Record<
  string,
  {
    agency: {
      displayValue?: string;
      selectedId?: string;
    };
  }
>;

export const MovePartner: FC<{ currentPartner: PartnerType }> = ({
  currentPartner,
}) => {
  const { user: currentUser } = useAuth0();

  const history = useHistory();
  const { toast } = useToast();

  const [{ loading: loadingPartners }, fetchPartners] = useLazyBaseApi<
    PartnerTypeaheadResponse
  >({});

  const [{ error: updatePartnerError }, updatePartnerAncestry] = useLazyBaseApi(
    {
      method: 'POST',
      url: `agent_services/partner/${currentPartner.id}/update_ancestry`,
    }
  );

  const showMovePartnerBanner =
    updatePartnerAncestryPolicy(currentUser!) && !currentPartner.isCarrier;

  const prefix = useMemo(() => new Date().getTime().toString(), []);
  const initialValues = useMemo(() => {
    return {
      // to prevent browser autofill on typeaheads
      [prefix]: {
        agency: {
          displayValue: '',
          selectedId: '',
        },
      },
    };
  }, [prefix]);

  const validations = Yup.object().shape({
    [prefix]: Yup.object().shape({
      agency: Yup.object().shape({
        selectedId: Yup.string().required('Agency is required'),
      }),
    }),
  });

  return showMovePartnerBanner ? (
    <>
      <HRule size="lg" />
      <Alert
        data-testid="move-partner-banner"
        message={`If this partner is no longer affiliated with the parent partner, please move them to the correct partner.`}
        title="Change Parent Partner"
        type="light-brand"
        className={styles.alert}
        action={
          <Modal
            variant="normal"
            toggle={openModal => (
              <Button
                data-testid="move-partner-open-modal-button"
                link
                variant="primary"
                size="sm"
                onClick={openModal}
                className={styles.action}
              >
                Change Parent Partner
              </Button>
            )}
          >
            {close => (
              <Formik<MovePartnerFormType>
                initialValues={initialValues}
                onSubmit={async values => {
                  try {
                    await updatePartnerAncestry({
                      data: {
                        new_parent_id: values[prefix].agency.selectedId,
                      },
                    });

                    toast({
                      message: 'Parent partner successfully updated',
                      persistOnNavigation: true,
                    });

                    history.replace(
                      `/admin/partners/${currentPartner.id}`,
                      { refreshData: true } // To trigger breadcrumbs to refresh data
                    );
                  } catch {}
                }}
                validationSchema={validations}
              >
                {({
                  values,
                  errors,
                  touched,
                  setFieldValue,
                  isSubmitting,
                  setValues,
                  handleSubmit,
                  handleBlur,
                }) => (
                  <div
                    data-testid="move-partner-modal"
                    className={classnames('fs-mask', styles.modalContainer)}
                  >
                    <h1 className={styles.heading}>
                      Move {currentPartner.name} to a different parent partner
                    </h1>
                    <Typeahead<PartnerTypeaheadResponse>
                      data-testid="partner-typeahead"
                      name={`${prefix}.agency.displayValue`}
                      label="Search Partner"
                      debounce={200}
                      value={values[prefix].agency.displayValue}
                      setValue={value => {
                        setFieldValue(`${prefix}.agency.displayValue`, value);
                      }}
                      errorMessage={
                        getIn(touched, `${prefix}.agency.displayValue`)
                          ? getIn(errors, `${prefix}.agency.selectedId`)
                          : undefined
                      }
                      loading={loadingPartners}
                      lazyRequest={fetchPartners}
                      getLazyRequestConfig={getFetchPartnersConfig}
                      isClearable={!!values[prefix].agency.displayValue}
                      disabled={isSubmitting}
                      onClear={() => {
                        setValues(initialValues);
                      }}
                      onBlur={e => {
                        if (
                          values[prefix].agency.displayValue &&
                          !values[prefix].agency.selectedId
                        ) {
                          setFieldValue(`${prefix}.agency.displayValue`, '');
                        }
                        handleBlur(e);
                      }}
                      onSelect={(selectedId, options) => {
                        const selectedOption = options.find(
                          option => option.id === selectedId
                        );

                        if (selectedOption) {
                          setValues({
                            [prefix]: {
                              agency: {
                                displayValue: selectedOption.name,
                                selectedId: selectedOption.id,
                              },
                            },
                          });
                        }
                      }}
                      transformOptions={(options: PartnerTypeaheadResponse) =>
                        options.reduce((acc, current) => {
                          return {
                            ...acc,
                            [current.id]: (
                              <TypeaheadResult primaryResult={current.name} />
                            ),
                          };
                        }, {})
                      }
                    />
                    <div className={styles.buttonContainer}>
                      <Button
                        block
                        data-testid="move-partner-cancel-button"
                        variant="secondary"
                        onClick={close}
                      >
                        Cancel
                      </Button>
                      <Button
                        block
                        data-testid="move-partner-confirm-button"
                        variant="primary"
                        isSubmitting={isSubmitting}
                        onClick={() => {
                          handleSubmit();
                        }}
                        disabled={!values[prefix].agency.selectedId}
                      >
                        Change Parent Partner
                      </Button>
                    </div>
                    {updatePartnerError && (
                      <Alert
                        className={styles.error}
                        message={updatePartnerError.message}
                      />
                    )}
                  </div>
                )}
              </Formik>
            )}
          </Modal>
        }
      />
    </>
  ) : null;
};
